import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Syringe, Plus, History, Settings as SettingsIcon, X, Save, Trash2, Sliders } from "lucide-react";
import ConfirmDialog from "./ConfirmDialog";

const HEADER = "#2D6E6E";
const TEAL = "#4A7C7C"; const AMBER = "#E8A838"; const RED = "#C0392B"; const TRACK = "rgba(74,124,124,0.18)";

const fmt = (iso) => { try { return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; } };

function Ring({ percent, color, label }) {
  const r = 45, c = 2 * Math.PI * r;
  const d = Math.max(0, Math.min(100, percent)) / 100 * c;
  return (
    <svg width={110} height={110} viewBox="0 0 110 110">
      <circle cx={55} cy={55} r={r} stroke={TRACK} strokeWidth={10} fill="none" />
      <circle cx={55} cy={55} r={r} stroke={color} strokeWidth={10} fill="none"
        strokeDasharray={`${d} ${c - d}`} strokeDashoffset={c / 4} strokeLinecap="round" />
      <text x="50%" y="46%" textAnchor="middle" style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontWeight: 700, fontSize: 18, fill: "#3B2F1E" }}>{label.top}</text>
      <text x="50%" y="62%" textAnchor="middle" style={{ fontSize: 10, fill: "#8C9E8A" }}>{label.bottom}</text>
    </svg>
  );
}

export default function VaccinationTracker({ horseId, horseName }) {
  const { user } = useAuth();
  const role = user?.role;
  const isOwnerClient = role === "owner";
  // ENH-OwnerWrite: owner is now a first-class writer for events + schedules on their own horses.
  const canWriteEvent = ["admin", "manager", "vet", "super_admin", "owner"].includes(role);
  const canAdminSchedules = ["admin", "manager", "super_admin", "owner"].includes(role);
  const canSeeCost = ["admin", "manager", "vet", "super_admin"].includes(role);

  const [summaries, setSummaries] = useState(null);
  const [vaccineTypes, setVaccineTypes] = useState([]);
  const [error, setError] = useState("");
  const [logFor, setLogFor] = useState(null);
  const [logForm, setLogForm] = useState({ date_given: new Date().toISOString().slice(0, 10), event_time: "", batch_number: "", cost: "", comment: "" });
  const [histFor, setHistFor] = useState(null);
  const [history, setHistory] = useState(null);
  const [delEv, setDelEv] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [scheduleRows, setScheduleRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setError("");
    try {
      const [s, vt] = await Promise.all([
        api.get(`/horses/${horseId}/vaccinations`),
        api.get("/vaccine-types"),
      ]);
      setSummaries(s.data);
      setVaccineTypes(vt.data);
    } catch (e) { setError(formatApiError(e)); setSummaries([]); }
  };

  useEffect(() => { if (horseId && role !== "driver") load(); }, [horseId, role]);

  const openManage = async () => {
    setManageOpen(true);
    try {
      const { data } = await api.get(`/horses/${horseId}/vaccine-schedules`);
      const byId = Object.fromEntries(data.map(s => [s.vaccine_type_id, s]));
      const rows = vaccineTypes.map(vt => ({
        vaccine_type_id: vt.id, vaccine_name: vt.name,
        default_days: vt.default_interval_days,
        active: byId[vt.id]?.active ?? false,
        interval_days: byId[vt.id]?.interval_days ?? "",
      }));
      setScheduleRows(rows);
    } catch (e) { setError(formatApiError(e)); }
  };

  const saveManage = async () => {
    setBusy(true);
    try {
      const payload = scheduleRows.map(r => ({
        vaccine_type_id: r.vaccine_type_id, active: !!r.active,
        interval_days: r.interval_days === "" ? null : Number(r.interval_days),
      }));
      await api.put(`/horses/${horseId}/vaccine-schedules`, payload);
      setManageOpen(false);
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submitLog = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { date_given: logForm.date_given, batch_number: logForm.batch_number, comment: logForm.comment };
      if (logForm.event_time) payload.event_time = logForm.event_time;
      if (canSeeCost && logForm.cost !== "") payload.cost = Number(logForm.cost);
      await api.post(`/horses/${horseId}/vaccine-types/${logFor.vaccine_type_id}/events`, payload);
      setLogFor(null);
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const openHistory = async (s) => {
    setHistFor(s); setHistory(null);
    try {
      const { data } = await api.get(`/horses/${horseId}/vaccine-types/${s.vaccine_type_id}/events`);
      setHistory(data);
    } catch (e) { setHistory([]); setError(formatApiError(e)); }
  };

  const removeEvent = async () => {
    try {
      await api.delete(`/vaccination-events/${delEv.id}`);
      setDelEv(null);
      if (histFor) await openHistory(histFor);
      await load();
    } catch (e) { setError(formatApiError(e)); setDelEv(null); }
  };

  if (role === "driver") return null;

  return (
    <div data-testid="vaccination-tracker">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div>
          <div className="se-label">Vaccinations</div>
          <h3 className="font-heading mt-1" style={{ fontSize: 22 }}>Vaccine schedule</h3>
        </div>
        {canAdminSchedules && (
          <button className="se-btn" onClick={openManage} style={{ background: HEADER }} data-testid="vaccinations-manage-button">
            <Sliders size={14} /> Manage vaccines
          </button>
        )}
      </div>

      {error && <div className="mb-3 text-sm px-3 py-2 rounded-lg" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="vaccinations-error">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="vaccinations-grid">
        {(summaries || []).length === 0 && summaries !== null && (
          <div className="se-card text-sm" style={{ color: "#8C9E8A" }} data-testid="vaccinations-empty">
            No vaccines activated for this horse yet. {canAdminSchedules ? "Click 'Manage vaccines' to enable some." : ""}
          </div>
        )}
        {(summaries || []).map((s) => {
          let percent = 0, color = TRACK, top = "—", bottom = "Not yet given";
          if (s.status !== "no_history") {
            percent = Math.min(100, (s.days_elapsed ?? 0) / s.interval_days * 100);
            color = s.status === "overdue" ? RED : s.status === "due_soon" ? AMBER : TEAL;
            if (s.status === "overdue") percent = 100;
            top = `${Math.max(0, s.days_elapsed ?? 0)}`;
            bottom = `/ ${s.interval_days} days`;
          }
          const badge = s.status === "overdue"
            ? <span className="se-badge se-badge-urgent">{s.vaccine_name} overdue</span>
            : s.status === "due_soon"
            ? <span className="se-badge se-badge-warning">Due soon</span>
            : null;
          return (
            <div key={s.schedule_id} className="se-card p-0 overflow-hidden flex flex-col" data-testid={`vaccine-card-${s.vaccine_type_id}`}>
              <div className="px-4 py-3 flex items-center justify-between" style={{ background: HEADER, color: "#F5EFE6" }}>
                <div className="flex items-center gap-2"><Syringe size={16} /><span className="font-medium">{s.vaccine_name}</span></div>
                {badge}
              </div>
              <div className="p-4 flex flex-col items-center gap-3 flex-1">
                <Ring percent={percent} color={color === TRACK ? "rgba(74,124,124,0.35)" : color} label={{ top, bottom }} />
                {!isOwnerClient ? (
                  <div className="text-center text-sm">
                    <div style={{ color: "#8C9E8A" }}>{s.last_event_date ? `Last: ${fmt(s.last_event_date)}` : "Never"}</div>
                    {s.next_due_date ? (
                      <div style={{ color: TEAL, fontWeight: 700, marginTop: 2 }}>Next: {fmt(s.next_due_date)}</div>
                    ) : (
                      <div style={{ color: "#B8610A", fontWeight: 700, marginTop: 2 }}>Schedule now</div>
                    )}
                  </div>
                ) : (
                  <div className="text-center text-sm" style={{ color: "#8C9E8A" }}>{s.vaccine_name}</div>
                )}
                <div className="flex items-center gap-2 pt-1">
                  {canWriteEvent && (
                    <button className="se-btn" style={{ padding: "6px 12px", fontSize: 13, background: HEADER }} onClick={() => { setLogFor(s); setLogForm({ date_given: new Date().toISOString().slice(0, 10), event_time: "", batch_number: "", cost: "", comment: "" }); }} data-testid={`vaccine-log-${s.vaccine_type_id}`}>
                      <Plus size={14} /> Log
                    </button>
                  )}
                  <button className="se-btn se-btn-ghost" style={{ padding: "6px 10px", fontSize: 13 }} onClick={() => openHistory(s)} data-testid={`vaccine-history-${s.vaccine_type_id}`}>
                    <History size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {logFor && (
        <div className="fixed inset-0 z-30 flex items-center justify-center p-4" style={{ background: "rgba(59,47,30,0.5)" }} onClick={() => setLogFor(null)}>
          <form className="se-card w-full max-w-md" onClick={(e) => e.stopPropagation()} onSubmit={submitLog} data-testid="vaccine-log-form">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading" style={{ fontSize: 18 }}>Log {logFor.vaccine_name}</h3>
              <button type="button" className="se-btn se-btn-ghost" onClick={() => setLogFor(null)} style={{ padding: 6 }}><X size={14} /></button>
            </div>
            <label className="se-label block mb-1">Date given</label>
            <input type="date" className="se-input mb-2" value={logForm.date_given} onChange={(e) => setLogForm({ ...logForm, date_given: e.target.value })} required data-testid="vaccine-log-date" />
            <label className="se-label block mb-1">Time (optional)</label>
            <input type="time" className="se-input mb-2" value={logForm.event_time} onChange={(e) => setLogForm({ ...logForm, event_time: e.target.value })} data-testid="vaccine-log-time" />
            <label className="se-label block mb-1">Batch number (optional)</label>
            <input className="se-input mb-2" value={logForm.batch_number} onChange={(e) => setLogForm({ ...logForm, batch_number: e.target.value })} data-testid="vaccine-log-batch" />
            <label className="se-label block mb-1">Comment (optional)</label>
            <textarea className="se-input" style={{ height: 70, padding: "10px 14px" }} value={logForm.comment} onChange={(e) => setLogForm({ ...logForm, comment: e.target.value })} data-testid="vaccine-log-comment" />
            {canSeeCost && (
              <>
                <label className="se-label block mb-1 mt-2">Cost (€) — optional</label>
                <input type="number" min="0" step="0.01" className="se-input" value={logForm.cost} onChange={(e) => setLogForm({ ...logForm, cost: e.target.value })} data-testid="vaccine-log-cost" />
              </>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="se-btn se-btn-ghost" onClick={() => setLogFor(null)}>Cancel</button>
              <button type="submit" className="se-btn" disabled={busy} style={{ background: HEADER }} data-testid="vaccine-log-submit">{busy ? "Saving…" : "Log event"}</button>
            </div>
          </form>
        </div>
      )}

      {histFor && (
        <div className="fixed inset-0 z-30 flex items-center justify-center p-4" style={{ background: "rgba(59,47,30,0.5)" }} onClick={() => setHistFor(null)}>
          <div className="se-card w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="vaccine-history-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading" style={{ fontSize: 18 }}>{histFor.vaccine_name} history</h3>
              <button type="button" className="se-btn se-btn-ghost" onClick={() => setHistFor(null)} style={{ padding: 6 }}><X size={14} /></button>
            </div>
            {history === null && <div className="text-sm" style={{ color: "#8C9E8A" }}>Loading…</div>}
            {history !== null && history.length === 0 && <div className="text-sm" style={{ color: "#8C9E8A" }}>No events.</div>}
            <div className="space-y-2">
              {(history || []).map(e => (
                <div key={e.id} className="flex items-start justify-between gap-3 p-3 rounded-lg" style={{ background: "rgba(74,124,124,0.08)", border: "0.5px solid rgba(74,124,124,0.2)" }}>
                  <div className="flex-1 min-w-0">
                    <div style={{ fontWeight: 600 }}>{fmt(e.date_given)}</div>
                    <div className="text-xs mt-0.5" style={{ color: "#8C9E8A" }}>
                      {e.administered_by_name || e.created_by_name} · Next due {fmt(e.next_due_date)}
                      {e.batch_number ? ` · Batch ${e.batch_number}` : ""}
                      {canSeeCost && e.cost != null ? ` · €${e.cost}` : ""}
                    </div>
                    {e.comment && <div className="text-sm mt-2 whitespace-pre-wrap">{e.comment}</div>}
                  </div>
                  {canAdminSchedules && (
                    <button className="se-btn se-btn-ghost" style={{ padding: 6 }} onClick={() => setDelEv(e)} aria-label="Delete event" data-testid={`vaccine-history-delete-${e.id}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {manageOpen && (
        <div className="fixed inset-0 z-30 flex items-center justify-center p-4" style={{ background: "rgba(59,47,30,0.5)" }} onClick={() => setManageOpen(false)}>
          <div className="se-card w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="vaccine-manage-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading" style={{ fontSize: 18 }}>Manage vaccines for {horseName}</h3>
              <button type="button" className="se-btn se-btn-ghost" onClick={() => setManageOpen(false)} style={{ padding: 6 }}><X size={14} /></button>
            </div>
            {scheduleRows === null && <div className="text-sm" style={{ color: "#8C9E8A" }}>Loading…</div>}
            {scheduleRows !== null && scheduleRows.length === 0 && <div className="text-sm" style={{ color: "#8C9E8A" }}>No vaccines in the library yet. Add some in Settings → Vaccine Library.</div>}
            <div className="space-y-2">
              {(scheduleRows || []).map((r, i) => (
                <div key={r.vaccine_type_id} className="flex items-center justify-between gap-3 p-3 rounded-lg" style={{ background: "rgba(74,124,124,0.06)", border: "0.5px solid rgba(74,124,124,0.18)" }}>
                  <div className="flex-1">
                    <div style={{ fontWeight: 600 }}>{r.vaccine_name}</div>
                    <div className="text-xs" style={{ color: "#8C9E8A" }}>Default {r.default_days} days</div>
                  </div>
                  <input
                    type="number" min={1} max={3650}
                    className="se-input" style={{ width: 100 }}
                    placeholder="override"
                    value={r.interval_days}
                    onChange={(e) => {
                      const v = e.target.value;
                      setScheduleRows(rows => rows.map((x, idx) => idx === i ? { ...x, interval_days: v } : x));
                    }}
                    data-testid={`vaccine-manage-interval-${r.vaccine_type_id}`}
                  />
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox" checked={r.active}
                      onChange={(e) => setScheduleRows(rows => rows.map((x, idx) => idx === i ? { ...x, active: e.target.checked } : x))}
                      data-testid={`vaccine-manage-active-${r.vaccine_type_id}`}
                    /> Active
                  </label>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="se-btn se-btn-ghost" onClick={() => setManageOpen(false)}>Cancel</button>
              <button type="button" className="se-btn" style={{ background: HEADER }} onClick={saveManage} disabled={busy} data-testid="vaccine-manage-save"><Save size={14} /> Save</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog open={!!delEv} onClose={() => setDelEv(null)} onConfirm={removeEvent} title="Delete this vaccination event?" description="It can be restored from the audit log." confirmLabel="Yes, delete" testid="vaccine-event-delete-confirm" />
    </div>
  );
}
